// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Which machine the front door is playing (M9-32, 08 §1).
//
// **One control, and it does not exist for a stranger.** 08 §1 gives the landing route no hero copy
// and one primary control, and that decision is not being reopened here: this renders only for a
// browser that has a draft — somebody who has opened the Builder and started a machine. Anybody
// else's front door is exactly what it was.
//
// **A visible switch rather than an implicit rule.** The alternative was "if a draft exists it is
// the active machine", which is fewer moving parts and worse in two ways: a stranger sent a link by
// an author would meet a half-finished table on the product's only shop window, and "which machine
// am I looking at" would go back to being a rule you have to remember instead of a thing you can
// read. The whole point of one active machine is that the answer is on screen.
//
// **It says what it is playing and what it would switch to.** A toggle labelled only with its
// destination is the classic ambiguity — half of everyone reads "Play NEON NOVA" as the current
// state — so the current machine is stated beside the button rather than inferred from it.
//
// **And a name is not enough to tell the two apart.** A draft opened from the flagship carries the
// flagship's title until its author changes it — measured, on the first draft this feature ever
// saved: `title: "NEON NOVA"`, one pop bumper heavier than the machine it was copied from. So the
// draft is *marked* rather than merely named, and the offer to switch to it prints a title only
// when that title would distinguish it from the machine already on screen.
//
// Discarding the draft is deliberately **not** here. It lives in the Builder, which is where an
// author manages their machine and where the two-click confirmation this product uses for
// destructive editing already lives (`BuilderRoute`'s "start a new machine"). From the player's
// side, "reset to the default machine" is this button, and it keeps the draft.

import { useLocalisation } from '../i18n/useLanguage'
import { writeActiveChoice, type ActiveChoice } from '../activeMachine'

export interface ActiveMachineSwitchProps {
  /** What is on screen now. */
  readonly choice: ActiveChoice
  /** What the machine on screen is called, from its own `meta.title`. */
  readonly playing: string
  /** What the draft is called, for the offer to switch to it. */
  readonly draftTitle: string
}

export function ActiveMachineSwitch(props: ActiveMachineSwitchProps): React.JSX.Element {
  const { t } = useLocalisation()
  const { choice, playing, draftTitle } = props
  const toDraft = choice === 'flagship'
  // A title worth printing is one that is not the name of the machine already on screen — and not
  // empty, because the Builder does not ask for one until publish.
  const distinct = draftTitle !== '' && draftTitle !== playing
  return (
    <p className="stage__active">
      <span className="stage__activeName">
        {t('stage.playing')}: <strong>{playing}</strong>
        {toDraft ? null : <> — {t('stage.your-draft')}</>}
      </span>
      <button
        type="button"
        className="stage__activeSwitch"
        title={toDraft ? t('stage.switch-to-draft-title') : t('stage.switch-to-flagship-title')}
        onClick={() => writeActiveChoice(toDraft ? 'draft' : 'flagship')}
      >
        {toDraft
          ? distinct
            ? `${t('stage.switch-to-draft')} — ${draftTitle}`
            : t('stage.switch-to-draft')
          : t('stage.switch-to-flagship')}
      </button>
    </p>
  )
}
